import type { LucideIcon } from 'lucide-react';

import { Badge } from '@/_shared/components/ui/badge';
import { cn } from '@/_shared/lib/utils';

interface StepCardProps {
  stepNumber: number;
  kind: string;
  title: string;
  icon: LucideIcon;
  /** Right-aligned header slot (e.g. a remove button). */
  action?: React.ReactNode;
  className?: string;
  /**
   * When true, the card shows a brief highlight ring — used (P12 §6.5) when RoutineBuilder freshly
   * fills this step via an agent patch, drawing the eye to what just changed.
   */
  highlight?: boolean;
  /** When true, the card shows a destructive (red) border + shadow — the step has a dry-run error. */
  hasError?: boolean;
  /** Optional footer content (e.g. the dry-run preview) — rendered below an edge-to-edge divider under
   *  a small "Preview" heading. Omit for no footer. */
  footer?: React.ReactNode;
  /** When true, a "Stale" badge is shown next to the footer's "Preview" heading (result is out of date). */
  footerStale?: boolean;
  children: React.ReactNode;
}

/** One card in the vertical step chain. Header shows "Step N · Kind" + title; body is freeform. */
export function StepCard({
  stepNumber,
  kind,
  title,
  icon: Icon,
  action,
  className,
  highlight = false,
  hasError = false,
  footer,
  footerStale = false,
  children,
}: StepCardProps): React.ReactElement {
  // A card with no body (e.g. the Condition step set to "Always") collapses to just its header —
  // no empty padded body and no dangling divider under the header.
  const hasBody = children != null && children !== false;
  const hasFooter = footer != null && footer !== false;
  return (
    <div
      className={cn(
        'flex flex-col rounded-2xl border bg-card transition-[box-shadow,border-color] duration-500',
        // Each state applies exactly one border-color + one shadow. shadow-base and shadow-primary/
        // -destructive are mutually exclusive: twMerge won't dedupe them, so applying two shadows would
        // let the base shadow win by CSS order and hide the highlight. Error takes precedence over
        // the (transient) agent-patch highlight.
        hasError
          ? 'border-destructive shadow-destructive'
          : highlight
            ? 'border-primary shadow-primary'
            : 'border-border shadow-base',
        className,
      )}
    >
      <div className={cn('flex items-center gap-3 px-4 py-3', hasBody && 'border-b')}>
        <span className="flex size-7 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Icon className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[0.6875rem] font-medium tracking-wider text-muted-foreground uppercase">
            <span>Step {stepNumber}</span>
            <span className="size-1 rounded-full bg-border" />
            <span>{kind}</span>
          </div>
          <h3 className="truncate text-sm font-semibold text-foreground">{title}</h3>
        </div>
        {action}
      </div>
      {hasBody && <div className="flex flex-col gap-3 p-4">{children}</div>}
      {hasFooter && (
        <div className="flex flex-col gap-2 border-t px-4 py-3">
          <div className="flex items-center gap-2">
            <p className="text-[0.6875rem] font-medium tracking-wider text-muted-foreground uppercase">Preview</p>
            {footerStale && <Badge variant="warning">Stale</Badge>}
          </div>
          {footer}
        </div>
      )}
    </div>
  );
}
